import { defineStore } from 'pinia'
import { shallowRef } from 'vue'

export type ToastTone = 'success' | 'error' | 'warning' | 'info'

export interface ToastContent {
  title: string
  message?: string
  duration?: number
  persistent?: boolean
}

export interface ToastInput extends ToastContent {
  tone: ToastTone
  id?: string
}

export interface ToastItem {
  id: string
  tone: ToastTone
  title: string
  message?: string
  duration: number | null
  persistent: boolean
  createdAt: number
}

const DEFAULT_DURATIONS: Record<ToastTone, number> = {
  success: 3800,
  info: 4600,
  warning: 5600,
  error: 7000
}

let toastSequence = 0

function createToastId() {
  toastSequence += 1
  return `tq-toast-${Date.now()}-${toastSequence}`
}

function normalizeContent(content: ToastContent | string, message?: string): ToastContent {
  if (typeof content === 'string') return message ? { title: content, message } : { title: content }
  return content
}

export const useToastStore = defineStore('toast', () => {
  const toasts = shallowRef<ToastItem[]>([])
  const timers = new Map<string, ReturnType<typeof setTimeout>>()

  function clearTimer(id: string) {
    const timer = timers.get(id)
    if (!timer) return
    clearTimeout(timer)
    timers.delete(id)
  }

  function dismiss(id: string) {
    clearTimer(id)
    toasts.value = toasts.value.filter(toast => toast.id !== id)
  }

  function scheduleDismiss(toast: ToastItem) {
    if (import.meta.server || toast.persistent || toast.duration === null) return
    timers.set(toast.id, setTimeout(() => dismiss(toast.id), toast.duration))
  }

  function show(input: ToastInput) {
    const id = input.id ?? createToastId()
    dismiss(id)

    const toast: ToastItem = {
      id,
      tone: input.tone,
      title: input.title,
      persistent: input.persistent ?? false,
      duration: input.persistent ? null : input.duration ?? DEFAULT_DURATIONS[input.tone],
      createdAt: Date.now()
    }

    if (input.message) toast.message = input.message

    toasts.value = [toast, ...toasts.value].slice(0, 5)
    scheduleDismiss(toast)
    return id
  }

  function success(content: ToastContent | string, message?: string) {
    return show({ ...normalizeContent(content, message), tone: 'success' })
  }

  function error(content: ToastContent | string, message?: string) {
    return show({ ...normalizeContent(content, message), tone: 'error' })
  }

  function warning(content: ToastContent | string, message?: string) {
    return show({ ...normalizeContent(content, message), tone: 'warning' })
  }

  function info(content: ToastContent | string, message?: string) {
    return show({ ...normalizeContent(content, message), tone: 'info' })
  }

  function clearAll() {
    for (const id of timers.keys()) clearTimer(id)
    toasts.value = []
  }

  return { toasts, show, success, error, warning, info, dismiss, clearAll }
})
